(ns minorodata.components.analytics-models.docs
  "Read-only reference for a registry model — the documentation the DS team
   authors in the model's `app_definition.json`: `description-long` (markdown),
   plus a `description` on every input/output table and column. Parameter docs
   come from a block's `:doc` and are rendered by the form generator itself.

   `tables-detail` is rendered inline on the create/edit Inputs & outputs step;
   `docs-button` opens the whole reference in a dialog from the catalogue, the
   create/edit drawers and an instance's overview.

   What counts as documentation lives in `minorodata.analytics-models.domain`,
   apart from the rendering so it can be tested without react-markdown."
  (:require ["lucide-react" :refer [BookOpen]]
            [clojure.string :as str]
            [minorodata.analytics-models.domain :as am.domain]
            [minorodata.components.markdown.core :refer [formatted-text]]
            [ui.button :as ui.button]
            [ui.card :as ui.card]
            [ui.dialog :as ui.dialog]
            [ui.typography :as typography]
            [ui.utils :refer [clsx]]))


;; -----------------------------------------------------------------------------
;; Table & column detail
;; -----------------------------------------------------------------------------

;; Manifests write `column-name`/`data-type`/`description`; the name and type
;; fallbacks predate this ns and are kept for older ones. `description` has only
;; ever been spelt one way.
(defn- column-name [c]
  (if (map? c) (or (:name c) (:column-name c) (:column c) (pr-str c)) (str c)))


(defn- column-type [c]
  (when (map? c) (or (:type c) (:data-type c) (:dtype c))))


(defn- column-description [c]
  (when (map? c) (:description c)))


(defn- table-detail
  "One table's reference card: its name, `description`, and each column's name,
   type and `description`."
  [{:keys [table-name description columns]} optional?]
  [ui.card/card
   [ui.card/card-content {:class "flex flex-col gap-3"}
    [:div {:class "flex items-center gap-2"}
     [ui.card/card-title {:class "text-sm"} table-name]
     (when optional?
       [ui.card/card-description "optional"])]
    (when-not (str/blank? description)
      [typography/p {:class "text-xs text-kleene-text-secondary"} description])
    (if (seq columns)
      [:ul {:class "flex flex-col divide-y"}
       ;; Keyed by index — a manifest can repeat a column name.
       (map-indexed
        (fn [i c]
          ^{:key i}
          [:li {:class "flex flex-col gap-0.5 py-1.5 first:pt-0 last:pb-0"}
           [:div {:class "flex items-baseline justify-between gap-4"}
            [:span (column-name c)]
            (when-let [t (column-type c)]
              [:span {:class "shrink-0 text-kleene-text-secondary font-mono"} t])]
           (when-not (str/blank? (column-description c))
             [:span {:class "text-xs text-kleene-text-secondary"} (column-description c)])])
        columns)]
      [:span {:class "text-xs text-kleene-text-secondary"} "No columns defined"])]])


(defn tables-detail
  "Input/output table reference for a model, side by side."
  [model]
  (let [{:keys [input-tables output-tables]} (:analytics-model-registry/table-definitions model)
        ;; Only inputs can be optional; outputs are always required.
        group (fn [title optional-allowed? tables]
                (when (seq tables)
                  [:div {:class "flex-1 flex flex-col gap-2"}
                   [typography/h3 title]
                   (for [t tables]
                     ^{:key (:table-name t)}
                     [table-detail t (and optional-allowed? (:can-be-empty t))])]))]
    [:div {:class "flex gap-4"}
     (group "Input tables" true input-tables)
     (group "Output tables" false output-tables)]))


;; -----------------------------------------------------------------------------
;; Full model reference
;; -----------------------------------------------------------------------------

(defn- params-detail
  "Parameter reference — each block's label and its `:doc` blurb."
  [model]
  (when-let [params (seq (am.domain/params model))]
    [:div {:class "flex flex-col gap-2"}
     [typography/h3 "Parameters"]
     [ui.card/card
      [ui.card/card-content {:class "flex flex-col divide-y divide-kleene-layout-border"}
       (for [{pname :name :keys [label doc required]} params]
         ^{:key pname}
         [:div {:class "flex flex-col gap-0.5 py-2 first:pt-0 last:pb-0"}
          [:div {:class "flex items-baseline gap-2"}
           ;; Prose about the form, not a form control — so a span, not a label.
           [:span {:class "text-sm font-medium"} (or label (name pname))]
           (when required
             [typography/small {:class "text-kleene-text-secondary"} "required"])]
          (when-not (str/blank? doc)
            [:span {:class "text-xs text-kleene-text-secondary"} doc])])]]]))


(defn- long-description
  "`description-long` is markdown authored by the DS team, so it renders through
   the app's existing `formatted-text` renderer — react-markdown without
   `rehype-raw`, so embedded HTML is escaped rather than injected. Falls back to
   the brief description when no long one is published."
  [model]
  (let [long-text  (:analytics-model-registry/description-long model)
        brief-text (:analytics-model-registry/description-brief model)]
    (cond
      (not (str/blank? long-text))
      [:div {:class "prose prose-sm max-w-none break-words dark:prose-invert"}
       [formatted-text {:text long-text}]]

      (not (str/blank? brief-text))
      [typography/p {:class "text-kleene-text-secondary"} brief-text])))


(defn model-docs
  "The model's full reference: long description, parameters, and I/O tables."
  [model]
  [:div {:class "flex flex-col gap-6"}
   [long-description model]
   [params-detail model]
   [tables-detail model]])


(defn docs-dialog
  "Opens `model-docs` in a dialog. `trigger` is the element that opens it."
  [model trigger]
  (let [title (or (:analytics-model-registry/display-name model)
                  (:analytics-model-registry/project-name model))
        version (:analytics-model-registry/version model)]
    [ui.dialog/dialog-root
     [ui.dialog/dialog-trigger trigger]
     [ui.dialog/dialog-content {:size :medium :data-cy "analytics-model-docs-dialog"}
      [ui.dialog/dialog-title (str title (when version (str " · " version)))]
      [:div {:class "mt-4"}
       [model-docs model]]]]))


(defn docs-button
  "Trigger for `docs-dialog`. Renders a labelled button when given a `:label`,
   an icon-only one otherwise. Renders nothing for an undocumented model.

   The dialog trigger clones this element (`as-child`) and hangs the click
   handler and aria state on it, so it must be a real `<button>`: a div would be
   neither tab-reachable nor focusable for Radix to restore focus to on close.
   The styled `ui.button` therefore renders as a span inside it."
  [{:keys [model label variant size class data-cy]}]
  (when (am.domain/documented? model)
    [docs-dialog model
     [:button {:type       "button"
               :aria-label (or label "View model documentation")
               :class      (clsx "rounded-lg cursor-pointer focus:outline-hidden"
                                 "focus-visible:ring-3 focus-visible:ring-kleene-neutral-04/30"
                                 class)}
      [ui.button/button
       {:variant   (or variant :empty-transparent)
        :size      (or size (if label :small :icon-md))
        :icon      BookOpen
        :component :span
        :data-cy   (or data-cy "analytics-model-docs-btn")}
       label]]]))
